import React, { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Box,
  Fade,
  CircularProgress
} from "@mui/material";

const LoginForm = ({ onLogin, onSwitchToRegister }) => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loggingIn, setLoggingIn] = useState(false);
  let loginTimeout;

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    if (loggingIn) return;

    clearTimeout(loginTimeout);
    loginTimeout = setTimeout(async () => {
      setLoggingIn(true);
      try {
        if (!identifier.trim()) throw new Error("Please enter your username or email.");
        if (!password) throw new Error("Password cannot be empty.");

        const response = await fetch(`${API_BASE_URL}/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ identifier: identifier.trim().toLowerCase(), password }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || "Invalid login credentials.");
        }

        const data = await response.json();
        setError(null);
        onLogin(data.token);
      } catch (err) {
        setError(err.message || "An unexpected error occurred. Please try again.");
      } finally {
        setLoggingIn(false);
      }
    }, 500); // Debounce for 500ms
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 2,
        background: "linear-gradient(120deg, #1a1a1a, #0d0d0d)",
        width: "calc(2 * 400px)",
        "@media (max-width: 800px)": {
          width: "100%",
        },
      }}
    >
      <Fade in timeout={1000}>
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            maxWidth: 400,
            width: "100%",
            backdropFilter: "blur(10px)",
            background: "rgba(30, 30, 30, 0.9)",
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          }}
        >
          <Box textAlign="center" mb={2}>
            <Box textAlign="center" mb={2}>
              <img
                src="/images/yiggy_banner.png"
                alt="Yiggy Banner"
                style={{ width: "150px", height: "50px" }}
              />
            </Box>
            <Typography
              variant="body1"
              gutterBottom
              sx={{
                fontWeight: "bold",
                color: "rgba(200, 200, 200, 1)",
                marginTop: 2,
                textShadow: "0px 0px 6px rgba(255, 255, 255, 0.2)",
              }}
            >
              The core of your workflow
            </Typography>
          </Box>
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                backgroundColor: "rgba(50, 50, 50, 0.9)",
                color: "#fff",
                textShadow: "1px 1px 5px rgba(0, 0, 0, 0.5)",
              }}
            >
              {error}
            </Alert>
          )}
          <form onSubmit={handleLogin}>
            <TextField
              label="Username or Email"
              variant="outlined"
              fullWidth
              margin="normal"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              sx={{
                input: { color: "#98FF98" },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
                "&:hover .MuiInputLabel-root": {
                  color: "#98FF98",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#98FF98",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.2)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.4)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#98FF98",
                  },
                },
                "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0px 1000px rgba(30, 30, 30, 0.9) inset",
                  WebkitTextFillColor: "#98FF98",
                  transition: "background-color 5000s ease-in-out 0s",
                },
              }}
            />
            <TextField
              label="Password"
              variant="outlined"
              type="password"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              sx={{
                input: { color: "#98FF98" },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
                "&:hover .MuiInputLabel-root": {
                  color: "#98FF98",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#98FF98",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.2)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.4)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#98FF98",
                  },
                },
                "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0px 1000px rgba(30, 30, 30, 0.9) inset",
                  WebkitTextFillColor: "#98FF98",
                  transition: "background-color 5000s ease-in-out 0s",
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loggingIn}
              sx={{
                mt: 2,
                py: 1.5,
                height: "60px",
                fontSize: "1rem",
                fontWeight: "bold",
                background: loggingIn ? "rgba(85, 85, 85, 0.3)" : "rgba(152, 255, 152, 0.5)",
                color: loggingIn ? "white" : "black",
                textShadow: "0px 0px 6px rgba(152, 255, 152, 0.1)",
                "&:hover": {
                  color: "black",
                  background: "rgba(152, 255, 152, 0.9)",
                },
              }}
            >
              {loggingIn ? <CircularProgress
              /> : "Login"}
            </Button>
          </form>
        </Paper>
      </Fade>
    </Box>
  );
};

export default LoginForm;